import { Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs';
import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';
import { EventBus } from '@core/modules/event-bus/event-bus';
import { AppEvents } from '@core/modules/event-bus/app-events';
import { KeyboardService } from '@core/services/keyboard.service';
import { KeyCode } from '@core/enums/key-parameters';

@Injectable({ providedIn: 'root' })
export class SidebarService {
  private readonly LOCAL_STORAGE_KEY = 'sidebar-is-collapsed';
  private readonly sidebarCollapsed = signal(false);
  private readonly activeRoute = signal<string>('');

  readonly sidebarCollapsed$ = toObservable(this.sidebarCollapsed);
  readonly routeChanged$ = toObservable(this.activeRoute);

  constructor(
    private readonly eventBus: EventBus,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly keyboardService: KeyboardService
  ) {
    const isCollapsed =
      sessionStorage.getItem(this.LOCAL_STORAGE_KEY) ?? 'true';
    const value = JSON.parse(isCollapsed);
    this.sidebarCollapsed.set(value);

    this.breakpointObserver
      .observe([
        Breakpoints.HandsetLandscape,
        Breakpoints.HandsetPortrait,
        Breakpoints.TabletPortrait,
      ])
      // Should skip first, because otherwise it will trigger directly and restoring of stored value will not work
      .pipe(skip(1))
      .subscribe(state => this.handleBreakpointEvent(state));

    this.keyboardService
      .onKeyCodeDown(KeyCode.B_Key)
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        if (!event.ctrlKey) return;
        this.handleShortcut();
      });
  }

  public open(): void {
    this.toggleSidebar(false);
    this.emitSidebarOpenedEvent();
  }

  public collapse(): void {
    this.toggleSidebar(true);
    this.emitSidebarClosedEvent();
  }

  private toggleSidebar(collapsed: boolean): void {
    this.sidebarCollapsed.set(collapsed);
    this.setValueInLocalStorage();
    this.emitSidebarToggledEvent();
  }

  private handleBreakpointEvent(state: BreakpointState) {
    if (state.matches) {
      this.sidebarCollapsed.set(true);
      this.emitSidebarClosedEvent();
    } else {
      this.sidebarCollapsed.set(false);
      this.emitSidebarOpenedEvent();
    }
    this.emitSidebarToggledEvent();
  }

  private setValueInLocalStorage(): void {
    sessionStorage.setItem(
      this.LOCAL_STORAGE_KEY,
      JSON.stringify(this.sidebarCollapsed())
    );
  }

  setActiveRoute(route: string, active: boolean) {
    if (active) this.activeRoute.set(route);
    else if (this.activeRoute() === route) this.activeRoute.set('');
  }

  private handleShortcut(): void {
    this.toggleSidebar(!this.sidebarCollapsed());
  }

  private emitSidebarToggledEvent(): void {
    this.eventBus.emit(new AppEvents.Sidebar.Toggled());
  }

  private emitSidebarOpenedEvent(): void {
    this.eventBus.emit(new AppEvents.Sidebar.Opened());
  }

  private emitSidebarClosedEvent(): void {
    this.eventBus.emit(new AppEvents.Sidebar.Closed());
  }
}
